import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import toast from "react-hot-toast";
import NCRAnnotatableImageUpload from "./NCRAnnotatableImageUpload";
import PermitSignatureModal from "../Safety/Permit_to_work/utils/PermitSignatureModal";
import { ArrowLeft, Send, ShieldAlert } from "lucide-react";
import { createNCR } from "../../services/ncrService";
import {
  getProjectsForCurrentUser,
  fetchTowersByProject,
  getProjectMakersForNCR,
  getUserAccessForProject,
} from "../../api";

export default function NCRCreatePage() {
  const navigate = useNavigate();

  let roleStr = localStorage.getItem("ROLE") || "";
  if (!roleStr) {
    try {
      const raw = localStorage.getItem("USER_DATA");
      if (raw) {
        const data = JSON.parse(raw);
        roleStr = data?.role || data?.roles?.[0] || "";
      }
    } catch (e) {}
  }
  const isMaker = (roleStr || "").toLowerCase().includes("maker");

  // Mock user and data
  const user = { name: "Current User" };

  const [projects, setProjects] = useState([]);
  const [towers, setTowers] = useState([]);

  const [projectsLoading, setProjectsLoading] = useState(false);
  const [towersLoading, setTowersLoading] = useState(false);

  const [makers, setMakers] = useState([]);
  const [makersLoading, setMakersLoading] = useState(false);
  const [makerSearch, setMakerSearch] = useState("");

  const [attachments, setAttachments] = useState([]);
  const [signature, setSignature] = useState("");
  const [showSigModal, setShowSigModal] = useState(false);
  const [saving, setSaving] = useState(false);

  const [values, setValues] = useState({
    ncr_no: "",
    doc_no: "",
    date: new Date().toISOString().slice(0, 10),
    related_to: "",
    classification: "major",
    identification: "",
    tower_id: "",
    location_text: "",
    project_id: "",
    root_cause: "",
    correction: "",
    corrective_action: "",
    preventive_action: "",
    follow_up_responsibility: "",
    verification_responsibility: "",
    assigned_makers: [],
    target_date: new Date(Date.now() + 7 * 86400000).toISOString().slice(0, 10),
  });

  const getCurrentUserId = () => {
    try {
      const raw = localStorage.getItem("USER_DATA");
      if (raw && raw !== "undefined") {
        const user = JSON.parse(raw);
        return (
          user?.id ||
          user?.user_id ||
          user?.pk ||
          user?.user?.id ||
          user?.user?.user_id ||
          localStorage.getItem("USER_ID") ||
          localStorage.getItem("user_id")
        );
      }
    } catch (error) {
      console.warn("Failed to parse USER_DATA", error);
    }
    return localStorage.getItem("USER_ID") || localStorage.getItem("user_id");
  };

  const normalizeAccessRole = (role) =>
    String(role || "")
      .trim()
      .toLowerCase()
      .replaceAll("-", "_")
      .replaceAll(" ", "_");

  const CREATE_NCR_ALLOWED_ROLES = new Set([
    "checker",
    "quality_engineer",
    "admin",
    "project_manager",
  ]);

  const extractAccessRows = (res) => {
    const data = res?.data ?? res;
    if (Array.isArray(data)) return data;
    if (Array.isArray(data?.results)) return data.results;
    if (Array.isArray(data?.data)) return data.data;
    if (Array.isArray(data?.accesses)) return data.accesses;
    if (data && typeof data === "object") return [data];
    return [];
  };

  const extractRolesFromAccess = (access) => {
    const roles = [];
    [
      access?.role,
      access?.role_name,
      access?.name,
      access?.user_role,
      access?.active_role,
    ].forEach((role) => {
      if (role) roles.push(normalizeAccessRole(role));
    });

    const nestedRoles =
      access?.roles ||
      access?.user_roles ||
      access?.access_roles ||
      access?.user_access_roles ||
      [];

    if (Array.isArray(nestedRoles)) {
      nestedRoles.forEach((roleObj) => {
        if (typeof roleObj === "string") {
          roles.push(normalizeAccessRole(roleObj));
        } else if (roleObj && typeof roleObj === "object") {
          [
            roleObj?.role,
            roleObj?.role_name,
            roleObj?.name,
            roleObj?.code,
          ].forEach((role) => {
            if (role) roles.push(normalizeAccessRole(role));
          });
        }
      });
    }
    return roles;
  };

  const hasCreateNCRAccess = (accessRows) => {
    if (!accessRows.length) return false;
    return accessRows.some((access) => {
      const roles = extractRolesFromAccess(access);
      return roles.some((role) => CREATE_NCR_ALLOWED_ROLES.has(role));
    });
  };

  const filterProjectsByUserAccess = async (projectList) => {
    const userId = getCurrentUserId();
    if (!userId) {
      toast.error("Unable to identify current user for project access.");
      return [];
    }

    const checks = await Promise.allSettled(
      projectList.map(async (project) => {
        const res = await getUserAccessForProject(userId, project.id);
        const accessRows = extractAccessRows(res);
        if (hasCreateNCRAccess(accessRows)) {
          return project;
        }
        return null;
      }),
    );

    return checks
      .filter((item) => item.status === "fulfilled" && item.value)
      .map((item) => item.value);
  };

  useEffect(() => {
    let isMounted = true;

    const loadProjects = async () => {
      setProjectsLoading(true);

      try {
        const res = await getProjectsForCurrentUser();

        const allProjects = unwrapList(res)
          .map(normalizeProject)
          .filter((project) => project.id);

        const accessedProjects = await filterProjectsByUserAccess(allProjects);

        if (isMounted) {
          setProjects(accessedProjects);

          if (!accessedProjects.length) {
            toast.error("No accessible projects found for NCR creation.");
          }
        }
      } catch (error) {
        console.error("Failed to load accessible projects", error);
        toast.error("Failed to load accessible projects");
      } finally {
        if (isMounted) {
          setProjectsLoading(false);
        }
      }
    };

    loadProjects();

    return () => {
      isMounted = false;
    };
  }, []);

  useEffect(() => {
    let isMounted = true;

    const loadTowers = async () => {
      if (!values.project_id) {
        setTowers([]);
        return;
      }

      setTowersLoading(true);

      try {
        const res = await fetchTowersByProject(values.project_id);
        const list = unwrapList(res).map(normalizeTower);

        if (isMounted) {
          setTowers(list);
        }
      } catch (error) {
        console.error("Failed to load towers", error);
        toast.error("Failed to load towers");
        if (isMounted) {
          setTowers([]);
        }
      } finally {
        if (isMounted) {
          setTowersLoading(false);
        }
      }
    };

    loadTowers();

    return () => {
      isMounted = false;
    };
  }, [values.project_id]);

  useEffect(() => {
    let isMounted = true;

    const loadAssignees = async () => {
      if (!values.project_id) {
        setMakers([]);
        return;
      }

      setMakersLoading(true);

      try {
        const res = await getProjectMakersForNCR(values.project_id);

        const rawList = unwrapList(res);
        const userMap = new Map();
        const currentUserId = getCurrentUserId();

        rawList.forEach((item) => {
          const assignee = normalizeAssignee(item);
          if (!assignee?.id) return;

          // Prevent self assignment: exclude the creator (currently logged-in user)
          if (currentUserId && String(assignee.id) === String(currentUserId)) {
            return;
          }

          const key = String(assignee.id);
          if (!userMap.has(key)) {
            userMap.set(key, assignee);
          } else {
            // If user already present, prioritize Maker role if user has maker access
            const existing = userMap.get(key);
            if (existing.role !== "maker" && assignee.role === "maker") {
              userMap.set(key, assignee);
            }
          }
        });

        const uniqueAssignees = Array.from(userMap.values()).sort((a, b) => {
          // Sort Makers first, then Checkers, then alphabetically
          if (a.role !== b.role) {
            return a.role === "maker" ? -1 : 1;
          }
          return a.name.localeCompare(b.name);
        });

        if (isMounted) {
          setMakers(uniqueAssignees);
        }
      } catch (error) {
        console.error("Failed to load project users", error);
        toast.error("Failed to load assignees for selected project");

        if (isMounted) {
          setMakers([]);
        }
      } finally {
        if (isMounted) {
          setMakersLoading(false);
        }
      }
    };

    loadAssignees();

    return () => {
      isMounted = false;
    };
  }, [values.project_id]);

  if (isMaker) {
    return (
      <div className="max-w-5xl mx-auto p-4 md:p-8 space-y-6 text-center py-24">
        <ShieldAlert className="w-12 h-12 text-slate-300 mx-auto mb-4" />
        <h2 className="text-xl font-display font-semibold text-slate-900">
          Access Denied
        </h2>
        <p className="text-slate-500 max-w-md mx-auto">
          Makers cannot create new Non-Conformity Reports.
        </p>
        <button
          className="mt-4 inline-flex items-center text-sm font-medium text-slate-500 hover:text-slate-900"
          onClick={() => navigate(-1)}
        >
          <ArrowLeft className="w-4 h-4 mr-2" /> Go Back
        </button>
      </div>
    );
  }

  const handleChange = (e) => {
    const { name, value } = e.target;

    setValues((prev) => {
      if (name === "project_id") {
        setMakerSearch("");

        return {
          ...prev,
          project_id: value,
          tower_id: "",
          assigned_makers: [],
        };
      }

      return {
        ...prev,
        [name]: value,
      };
    });
  };

  const selectedMakerIds = new Set(
    (values.assigned_makers || []).map((maker) => String(maker.id)),
  );

  const filteredMakers = (makers || []).filter((maker) => {
    const currentUserId = getCurrentUserId();
    if (currentUserId && String(maker.id) === String(currentUserId)) {
      return false;
    }

    const search = makerSearch.trim().toLowerCase();

    if (!search) return true;

    return (
      String(maker.name || "").toLowerCase().includes(search) ||
      String(maker.designation || "").toLowerCase().includes(search) ||
      String(maker.role || "").toLowerCase().includes(search)
    );
  });

  const handleAddMaker = (maker) => {
    if (!maker?.id) return;

    const currentUserId = getCurrentUserId();
    if (currentUserId && String(maker.id) === String(currentUserId)) {
      toast.error("Self-assignment is not allowed.");
      return;
    }

    setValues((prev) => {
      const exists = (prev.assigned_makers || []).some(
        (item) => String(item.id) === String(maker.id),
      );

      if (exists) return prev;

      const isChecker =
        String(maker.role || "").toLowerCase().includes("checker") ||
        String(maker.role || "").toLowerCase().includes("quality");
      const role = isChecker ? "checker" : "maker";

      return {
        ...prev,
        assigned_makers: [
          ...(prev.assigned_makers || []),
          {
            id: maker.id,
            name: maker.name,
            username: maker.username || maker.name,
            designation: maker.designation || (role === "checker" ? "Checker" : "Maker"),
            role: role,
            roleLabel: role === "checker" ? "Checker (Read-Only)" : "Maker",
          },
        ],
      };
    });

    setMakerSearch("");
  };

  const handleRemoveMaker = (makerId) => {
    setValues((prev) => ({
      ...prev,
      assigned_makers: (prev.assigned_makers || []).filter(
        (maker) => String(maker.id) !== String(makerId),
      ),
    }));
  };

  const formatDateForApi = (dateValue) => {
    if (!dateValue) return "";

    // input type="date" gives YYYY-MM-DD
    const [year, month, day] = dateValue.split("-");

    if (!year || !month || !day) return dateValue;

    return `${day}-${month}-${year}`;
  };

  const submitNCR = async (draft, signatureOverride = null) => {
    const finalSignature = signatureOverride || signature;

    if (!draft && !finalSignature) {
      setShowSigModal(true);
      return;
    }

    // Basic manual validation matching standard HTML required attributes
    if (!draft) {
      if (!values.assigned_makers || values.assigned_makers.length === 0) {
        toast.error("Please assign at least one Maker to the NCR.");
        return;
      }

      const hasMaker = (values.assigned_makers || []).some(
        (m) => m.role === "maker"
      );
      if (!hasMaker) {
        toast.error("At least one Maker must be assigned to the NCR.");
        return;
      }

      const requiredFields = [
        "related_to",
        "identification",
        "tower_id",
        "root_cause",
        "correction",
        "corrective_action",
        "follow_up_responsibility",
        "verification_responsibility",
        "target_date",
      ];
      const missing = requiredFields.filter(
        (f) => !values[f] || values[f].trim() === "",
      );
      if (missing.length > 0) {
        toast.error("Please fill in all required fields.");
        return;
      }
    }

    setSaving(true);
    try {
      const fd = new FormData();

      if (values.ncr_no && values.ncr_no.trim()) {
        fd.append("ncr_no", values.ncr_no.trim());
      }
      // Send current date as requested
      fd.append("date", new Date().toISOString().slice(0, 10));

      fd.append("project_id", values.project_id);
      const selectedProject = projects.find(
        (p) => String(p.id) === String(values.project_id),
      );

      fd.append("project_name", selectedProject?.name || "Unknown Project");
      if (selectedProject?.organization_id) {
        fd.append("organization_id", selectedProject.organization_id);
      } else if (selectedProject?.organization) {
        fd.append("organization_id", selectedProject.organization);
      }

      const selectedTower = towers.find(
        (t) => String(t.id) === String(values.tower_id),
      );

      if (values.tower_id) {
        fd.append("tower_id", values.tower_id);
        fd.append("tower_name", selectedTower?.name || "Unknown Tower");
      }
      if (values.location_text) {
        fd.append("location_text", values.location_text);
      }

      const assignedMakers = (values.assigned_makers || []).map((maker) => {
        const isChecker =
          String(maker.role || "").toLowerCase().includes("checker") ||
          String(maker.role || "").toLowerCase().includes("quality");
        return {
          maker_id: Number(maker.id),
          maker_name: maker.username || maker.name || "Unknown Assignee",
          maker_role: isChecker ? "checker" : "maker",
        };
      });

      fd.append("assigned_makers", JSON.stringify(assignedMakers));

      fd.append("non_conformity_related_to", values.related_to);
      fd.append("identification_of_non_conformance", values.identification);
      fd.append("classification", values.classification);
      fd.append("root_cause_analysis", values.root_cause);
      fd.append("correction", values.correction);
      fd.append("corrective_action", values.corrective_action);
      if (values.preventive_action)
        fd.append("preventive_action", values.preventive_action);
      fd.append("follow_up_responsibility", values.follow_up_responsibility);
      fd.append(
        "verification_responsibility",
        values.verification_responsibility,
      );
      fd.append(
        "target_date_of_compliance",
        formatDateForApi(values.target_date),
      );

      const dataUrlToFile = (dataUrl, filename) => {
        const arr = dataUrl.split(",");
        const mime = arr[0].match(/:(.*?);/)[1];
        const bstr = atob(arr[1]);
        let n = bstr.length;
        const u8arr = new Uint8Array(n);
        while (n--) {
          u8arr[n] = bstr.charCodeAt(n);
        }
        return new File([u8arr], filename, { type: mime });
      };

      if (finalSignature) {
        fd.append(
          "checker_signature",
          dataUrlToFile(finalSignature, "checker_signature.png"),
        );
      }



      attachments.forEach((att) => {
        if (att.file) {
          fd.append("identification_images", att.file);
        }
      });

      const res = await createNCR(fd);

      toast.success(draft ? "Draft saved" : "NCR submitted successfully");
      navigate("/NCR");
    } catch (e) {
      console.error(e);
      const data = e?.response?.data;
      const message =
        data?.assigned_makers?.[0] ||
        data?.ncr_no?.[0] ||
        data?.detail ||
        data?.non_field_errors?.[0] ||
        "An error occurred during submission";
      toast.error(message);
    } finally {
      setSaving(false);
    }
  };

  const onSubmitForm = (e) => {
    e.preventDefault(); // Prevent standard form submission
    if (!signature) {
      setShowSigModal(true);
      return;
    }
    submitNCR(false);
  };

  const handleSignatureSuccess = async (dataUrl) => {
    setSignature(dataUrl);
    setShowSigModal(false);
    submitNCR(false, dataUrl);
  };

  const unwrapList = (res) => {
    const data = res?.data ?? res;

    if (Array.isArray(data)) return data;
    if (Array.isArray(data?.results)) return data.results;
    if (Array.isArray(data?.data)) return data.data;
    if (Array.isArray(data?.projects)) return data.projects;
    if (Array.isArray(data?.buildings)) return data.buildings;

    return [];
  };

  const normalizeProject = (project) => ({
    id: project.id ?? project.project_id,
    name:
      project.name ??
      project.project_name ??
      project.title ??
      `Project ${project.id ?? project.project_id}`,
    organization_id: project.organization_id ?? project.organization,
  });

  const normalizeTower = (tower) => ({
    id: tower.id ?? tower.building_id ?? tower.tower_id,
    name:
      tower.name ??
      tower.building_name ??
      tower.tower_name ??
      `Tower ${tower.id ?? tower.building_id ?? tower.tower_id}`,
  });

  const normalizeRole = (role) =>
    String(role || "")
      .trim()
      .toLowerCase()
      .replaceAll("-", "_")
      .replaceAll(" ", "_");

  const extractRoles = (item) => {
    const roles = [];

    [
      item?.role,
      item?.role_name,
      item?.user_role,
      item?.active_role,
      item?.code,
    ].forEach((role) => {
      if (role) roles.push(normalizeRole(role));
    });

    const nestedRoles =
      item?.roles ||
      item?.user_roles ||
      item?.access_roles ||
      item?.user_access_roles ||
      [];

    if (Array.isArray(nestedRoles)) {
      nestedRoles.forEach((roleObj) => {
        if (typeof roleObj === "string") {
          roles.push(normalizeRole(roleObj));
        } else if (roleObj && typeof roleObj === "object") {
          [
            roleObj?.role,
            roleObj?.role_name,
            roleObj?.code,
          ].forEach((role) => {
            if (role) roles.push(normalizeRole(role));
          });
        }
      });
    }

    return roles;
  };

  const CHECKER_ROLES_SET = new Set([
    "checker",
    "quality_engineer",
    "quality engineer",
    "qa",
    "qaqc",
    "qa_qc",
    "quality",
    "admin",
    "project_manager",
    "project_head",
    "project head",
  ]);

  const isCheckerAccess = (item) => {
    const roles = extractRoles(item);
    if (roles.some((r) => CHECKER_ROLES_SET.has(r))) return true;

    const roleText = String(
      item?.role ||
        item?.role_name ||
        item?.user_role ||
        item?.active_role ||
        item?.designation ||
        "",
    )
      .toLowerCase()
      .replaceAll("-", "_")
      .replaceAll(" ", "_");

    return (
      roleText.includes("checker") ||
      roleText.includes("quality") ||
      roleText.includes("qa") ||
      roleText.includes("admin") ||
      roleText.includes("manager")
    );
  };

  const isMakerAccess = (item) => {
    const roles = extractRoles(item);
    if (roles.includes("maker")) return true;

    const roleText = String(
      item?.role ||
        item?.role_name ||
        item?.user_role ||
        item?.active_role ||
        item?.designation ||
        "",
    )
      .toLowerCase()
      .replaceAll("-", "_")
      .replaceAll(" ", "_");

    return roleText.includes("maker");
  };

  const normalizeAssignee = (item) => {
    const userObj = item?.user || item?.user_details || item?.employee || item;

    const id =
      item?.user_id ??
      item?.maker_id ??
      userObj?.id ??
      userObj?.user_id ??
      item?.id;

    if (!id) return null;

    const isMaker = isMakerAccess(item);
    const isChecker = isCheckerAccess(item);

    if (!isMaker && !isChecker) return null;

    // Strict role determination:
    // If user has checker access and NOT maker access -> strictly "checker"
    // If user has maker access and NOT checker access -> strictly "maker"
    // If user has maker access -> "maker", else "checker"
    let role = "maker";
    if (isChecker && !isMaker) {
      role = "checker";
    } else if (isMaker) {
      role = "maker";
    } else if (isChecker) {
      role = "checker";
    }

    const firstName = userObj?.first_name ?? item?.first_name ?? "";
    const lastName = userObj?.last_name ?? item?.last_name ?? "";
    const fullName = `${firstName} ${lastName}`.trim();

    const username = (
      userObj?.username ||
      item?.username ||
      userObj?.user_name ||
      item?.user_name ||
      ""
    ).trim();

    const name =
      username ||
      userObj?.full_name ||
      userObj?.name ||
      fullName ||
      item?.name ||
      `User ${id}`;

    const rawDesignation =
      item?.designation ||
      item?.role_name ||
      item?.role ||
      (role === "maker" ? "Maker" : "Checker");

    return {
      id,
      name,
      username: username || name,
      designation: rawDesignation,
      role, // 'maker' or 'checker'
      roleLabel: role === "maker" ? "Maker" : "Checker (Read-Only)",
    };
  };

  const normalizeMaker = normalizeAssignee;

  return (
    <div className="max-w-5xl mx-auto p-4 md:p-8 space-y-8">
      <div className="flex items-center justify-between gap-4 flex-wrap">
        <div>
          <button
            onClick={() => navigate(-1)}
            className="text-sm text-slate-500 hover:text-slate-900 inline-flex items-center gap-1 mb-2"
            data-testid="back-btn"
          >
            <ArrowLeft className="w-4 h-4" /> Back
          </button>
          <h1
            className="text-3xl font-display font-bold tracking-tight text-slate-900"
            data-testid="ncr-create-title"
          >
            Create Non-Conformity Report
          </h1>
          <p className="text-sm text-slate-500 mt-1">
            Complete all sections and submit for Project Head approval
          </p>
        </div>
      </div>

      <form id="ncr-form" className="space-y-6" onSubmit={onSubmitForm}>
        {/* Section A */}
        <Section title="A. Basic Information" step="A">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <Field
              label="Non-Conformity Report No."
              testId="field-ncr-no"
            >
              <input
                type="text"
                name="ncr_no"
                value={values.ncr_no}
                onChange={handleChange}
                placeholder="Enter NCR No. (Leave blank to auto-generate)"
                className="flex h-9 w-full rounded-md border border-slate-200 bg-transparent px-3 py-1 text-sm shadow-sm transition-colors focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-slate-950 disabled:cursor-not-allowed disabled:opacity-50"
                data-testid="input-ncr-no"
              />
            </Field>
            <Field
              label="Non-Conformity Related To"
              required
              testId="field-related-to"
            >
              <input
                type="text"
                name="related_to"
                value={values.related_to}
                onChange={handleChange}
                placeholder="e.g. Concrete Work, Safety, Material Inspection"
                className="flex h-9 w-full rounded-md border border-slate-200 bg-transparent px-3 py-1 text-sm shadow-sm transition-colors focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-slate-950 disabled:cursor-not-allowed disabled:opacity-50"
                required
                data-testid="input-related-to"
              />
            </Field>
            <Field
              label="Classification"
              required
              testId="field-classification"
            >
              <div className="flex gap-6 pt-2">
                <label
                  className="flex items-center gap-2 cursor-pointer group"
                  data-testid="radio-major"
                >
                  <input
                    type="radio"
                    name="classification"
                    value="major"
                    checked={values.classification === "major"}
                    onChange={handleChange}
                    className="w-4 h-4 text-blue-600 border-slate-300 focus:ring-blue-500"
                  />
                  <span className="text-red-600 font-medium">Major</span>
                </label>
                <label
                  className="flex items-center gap-2 cursor-pointer group"
                  data-testid="radio-minor"
                >
                  <input
                    type="radio"
                    name="classification"
                    value="minor"
                    checked={values.classification === "minor"}
                    onChange={handleChange}
                    className="w-4 h-4 text-blue-600 border-slate-300 focus:ring-blue-500"
                  />
                  <span className="text-green-600 font-medium">Minor</span>
                </label>
              </div>
            </Field>
            <div className="md:col-span-2 grid grid-cols-1 md:grid-cols-3 gap-6">
              <Field label="Project" required testId="field-project">
                <select
                  name="project_id"
                  value={values.project_id}
                  onChange={handleChange}
                  className="flex h-9 w-full rounded-md border border-slate-200 bg-transparent px-3 py-1 text-sm shadow-sm transition-colors focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-slate-950 disabled:cursor-not-allowed disabled:opacity-50"
                  required
                  disabled={projectsLoading}
                  data-testid="select-project"
                >
                  <option value="">
                    {projectsLoading ? "Loading projects..." : "Select project"}
                  </option>

                  {(projects || []).map((p) => (
                    <option key={p.id} value={p.id}>
                      {p.name}
                    </option>
                  ))}
                </select>
              </Field>
              <Field label="Tower" required testId="field-tower">
                <select
                  name="tower_id"
                  value={values.tower_id}
                  onChange={handleChange}
                  className="flex h-9 w-full rounded-md border border-slate-200 bg-transparent px-3 py-1 text-sm shadow-sm transition-colors focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-slate-950 disabled:cursor-not-allowed disabled:opacity-50"
                  required
                  disabled={!values.project_id || towersLoading}
                  data-testid="select-tower"
                >
                  <option value="">
                    {!values.project_id
                      ? "Select project first"
                      : towersLoading
                        ? "Loading towers..."
                        : "Select tower"}
                  </option>

                  {(towers || []).map((tower) => (
                    <option key={tower.id} value={tower.id}>
                      {tower.name}
                    </option>
                  ))}
                </select>
              </Field>
              <Field label="Location" testId="field-location">
                <input
                  type="text"
                  name="location_text"
                  value={values.location_text}
                  onChange={handleChange}
                  placeholder="e.g. Floor 3, Room 302"
                  className="flex h-9 w-full rounded-md border border-slate-200 bg-transparent px-3 py-1 text-sm shadow-sm transition-colors focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-slate-950 disabled:cursor-not-allowed disabled:opacity-50"
                  data-testid="input-location"
                />
              </Field>
            </div>
            <Field
              label="Identification of Non-Conformance"
              required
              testId="field-identification"
              className="md:col-span-2"
            >
              <textarea
                rows={3}
                name="identification"
                value={values.identification}
                onChange={handleChange}
                placeholder="Describe the non-conformance in detail"
                className="flex min-h-[60px] w-full rounded-md border border-slate-200 bg-transparent px-3 py-2 text-sm shadow-sm focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-slate-950 disabled:cursor-not-allowed disabled:opacity-50"
                required
                data-testid="input-identification"
              />
            </Field>
          </div>
        </Section>

        {/* Section B */}
        <Section title="B. Root Cause Analysis" step="B">
          <Field label="Root Cause Analysis" required testId="field-root-cause">
            <NumberedTextArea
              rows={4}
              name="root_cause"
              value={values.root_cause}
              onChange={handleChange}
              placeholder="Explain the root cause"
              required
              testId="input-root-cause"
            />
          </Field>
          <div className="mt-6">
            <Field
                label="Attachments (Photos / PDF)"
                testId="field-attachments"
              >
                <NCRAnnotatableImageUpload
                  value={attachments}
                  onChange={setAttachments}
                  testId="attach-upload"
                  label=""
                />
              </Field>
          </div>
        </Section>

        {/* Section C */}
        <Section title="C. Correction Details" step="C">
          <div className="grid grid-cols-1 gap-6">
            <Field label="Correction" required testId="field-correction">
              <textarea
                rows={3}
                name="correction"
                value={values.correction}
                onChange={handleChange}
                placeholder="Immediate correction taken"
                className="flex min-h-[60px] w-full rounded-md border border-slate-200 bg-transparent px-3 py-2 text-sm shadow-sm focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-slate-950 disabled:cursor-not-allowed disabled:opacity-50"
                required
                data-testid="input-correction"
              />
            </Field>
            <Field
              label="Corrective Action"
              required
              testId="field-corrective-action"
            >
              <NumberedTextArea
                rows={3}
                name="corrective_action"
                value={values.corrective_action}
                onChange={handleChange}
                required
                testId="input-corrective-action"
              />
            </Field>
            <Field label="Preventive Action" testId="field-preventive-action">
              <NumberedTextArea
                rows={3}
                name="preventive_action"
                value={values.preventive_action}
                onChange={handleChange}
                testId="input-preventive-action"
              />
            </Field>
          </div>
        </Section>

        {/* Section D */}
        <Section title="D. Responsibility & Assignment" step="D">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <Field
              label="Follow-up Responsibility"
              required
              testId="field-followup"
            >
              <input
                type="text"
                name="follow_up_responsibility"
                value={values.follow_up_responsibility}
                onChange={handleChange}
                placeholder="e.g. Site Engineer"
                className="flex h-9 w-full rounded-md border border-slate-200 bg-transparent px-3 py-1 text-sm shadow-sm transition-colors focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-slate-950 disabled:cursor-not-allowed disabled:opacity-50"
                required
                data-testid="input-followup"
              />
            </Field>
            <Field
              label="Verification Responsibility"
              required
              testId="field-verification"
            >
              <input
                type="text"
                name="verification_responsibility"
                value={values.verification_responsibility}
                onChange={handleChange}
                placeholder="e.g. QA/QC Engineer"
                className="flex h-9 w-full rounded-md border border-slate-200 bg-transparent px-3 py-1 text-sm shadow-sm transition-colors focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-slate-950 disabled:cursor-not-allowed disabled:opacity-50"
                required
                data-testid="input-verification"
              />
            </Field>
            <Field label="Assign NCR To" required testId="field-assignees">
              <div className="space-y-2">
                <input
                  type="text"
                  value={makerSearch}
                  onChange={(e) => setMakerSearch(e.target.value)}
                  disabled={!values.project_id || makersLoading}
                  placeholder={
                    !values.project_id
                      ? "Select project first"
                      : makersLoading
                        ? "Loading project users..."
                        : "Search by name or role (Maker / Checker)"
                  }
                  className="flex h-9 w-full rounded-md border border-slate-200 bg-transparent px-3 py-1 text-sm shadow-sm transition-colors focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-slate-950 disabled:cursor-not-allowed disabled:opacity-50"
                  data-testid="input-maker-search"
                />

                {values.project_id && !makersLoading && (
                  <div
                    className="max-h-52 overflow-y-auto rounded-md border border-slate-200 bg-white shadow-sm divide-y divide-slate-100"
                    data-testid="maker-search-results"
                  >
                    {filteredMakers.length > 0 ? (
                      filteredMakers.map((maker) => {
                        const selected = selectedMakerIds.has(String(maker.id));
                        const isCheckerRole = maker.role === "checker";

                        return (
                          <button
                            key={maker.id}
                            type="button"
                            onClick={() => {
                              if (!selected) {
                                handleAddMaker(maker);
                              }
                            }}
                            disabled={selected}
                            className={`flex w-full items-center justify-between px-3 py-2.5 text-left text-sm hover:bg-slate-50 transition-colors disabled:cursor-not-allowed disabled:bg-slate-50 ${
                              selected ? "text-slate-400" : "text-slate-700"
                            }`}
                            data-testid={`assignee-option-${maker.id}`}
                          >
                            <span className="flex items-center gap-2">
                              <span className="font-medium text-slate-900">{maker.name}</span>
                              <span
                                className={`inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-semibold uppercase tracking-wider ${
                                  isCheckerRole
                                    ? "bg-purple-50 text-purple-700 border border-purple-200"
                                    : "bg-blue-50 text-blue-700 border border-blue-200"
                                }`}
                              >
                                {isCheckerRole ? "Checker" : "Maker"}
                              </span>
                              {maker.designation && maker.designation !== "Maker" && maker.designation !== "Checker" && (
                                <span className="text-xs text-slate-400">
                                  ({maker.designation})
                                </span>
                              )}
                            </span>

                            <span className="text-xs font-medium text-blue-600">
                              {selected ? "Selected" : "+ Add"}
                            </span>
                          </button>
                        );
                      })
                    ) : (
                      <div className="px-3 py-2 text-sm text-slate-400">
                        No makers or checkers found for this project.
                      </div>
                    )}
                  </div>
                )}

                {(values.assigned_makers || []).length > 0 && (
                  <div
                    className="flex flex-wrap gap-2 pt-1"
                    data-testid="selected-assignees"
                  >
                    {values.assigned_makers.map((assignee) => {
                      const isCheckerRole = assignee.role === "checker";

                      return (
                        <span
                          key={assignee.id}
                          className={`inline-flex items-center gap-1.5 rounded-full border px-3 py-1 text-xs font-medium ${
                            isCheckerRole
                              ? "border-purple-200 bg-purple-50 text-purple-800"
                              : "border-blue-200 bg-blue-50 text-blue-800"
                          }`}
                          data-testid={`selected-assignee-${assignee.id}`}
                        >
                          <span className="font-semibold">{assignee.name}</span>
                          <span
                            className={`px-1.5 py-0.2 rounded text-[9px] uppercase font-bold tracking-wider ${
                              isCheckerRole
                                ? "bg-purple-200/60 text-purple-900"
                                : "bg-blue-200/60 text-blue-900"
                            }`}
                          >
                            {isCheckerRole ? "Checker (Read-Only)" : "Maker"}
                          </span>

                          <button
                            type="button"
                            onClick={() => handleRemoveMaker(assignee.id)}
                            className="ml-1 text-slate-400 hover:text-red-600 font-bold"
                            aria-label={`Remove ${assignee.name}`}
                          >
                            &times;
                          </button>
                        </span>
                      );
                    })}
                  </div>
                )}

                {(values.assigned_makers || []).length > 0 && (
                  <p className="text-xs text-slate-500">
                    {values.assigned_makers.filter((m) => m.role === "maker").length} Maker(s),{" "}
                    {values.assigned_makers.filter((m) => m.role === "checker").length} Checker(s) assigned.
                  </p>
                )}

                <p className="text-xs text-slate-400">
                  Assign one or more Makers (required for action) and optional Read-Only Checkers with access to this project.
                </p>
              </div>
            </Field>
            <Field
              label="Target Date of Compliance"
              required
              testId="field-target-date"
            >
              <input
                type="date"
                name="target_date"
                value={values.target_date}
                onChange={handleChange}
                min={new Date().toISOString().slice(0, 10)}
                className="flex h-9 w-full rounded-md border border-slate-200 bg-transparent px-3 py-1 text-sm shadow-sm transition-colors focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-slate-950 disabled:cursor-not-allowed disabled:opacity-50"
                required
                data-testid="input-target-date"
              />
            </Field>
          </div>
        </Section>

        {/* Section E */}
        {/* <Section title="E. Submission" step="E">
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                            <Field label="Submitted By" testId="field-submitted-by">
                                <input type="text" disabled value={user?.name || ""} className="flex h-9 w-full rounded-md border border-slate-200 bg-slate-100 px-3 py-1 text-sm shadow-sm transition-colors disabled:cursor-not-allowed disabled:opacity-50 text-slate-500" />
                            </Field>
                            <Field label="Submitted Date" testId="field-submitted-date">
                                <input type="text" disabled value={new Date().toLocaleString("en-GB")} className="flex h-9 w-full rounded-md border border-slate-200 bg-slate-100 px-3 py-1 text-sm shadow-sm transition-colors disabled:cursor-not-allowed disabled:opacity-50 text-slate-500" />
                            </Field>
                            <div className="md:col-span-2">
                                <label className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70 flex items-center gap-1 mb-2">
                                    Digital Signature <span className="text-red-500">*</span>
                                </label>
                                <SignaturePadInput value={signature} onChange={setSignature} testId="creator-signature" />
                            </div>
                        </div>
                    </Section> */}
        <div className="flex justify-end items-center gap-4 pt-4 border-t border-slate-200">
          <button
            type="submit"
            disabled={saving}
            className="inline-flex items-center justify-center rounded-md text-sm font-medium transition-colors bg-blue-600 text-white hover:bg-blue-700 h-10 px-6 py-2 shadow disabled:opacity-50 disabled:cursor-not-allowed"
            data-testid="submit-ncr-btn"
          >
            <Send className="w-4 h-4 mr-2" /> Submit
          </button>
        </div>
      </form>

      <PermitSignatureModal
        isOpen={showSigModal}
        onClose={() => setShowSigModal(false)}
        onSignatureSuccess={handleSignatureSuccess}
        actionTitle="Submit NCR"
      />
    </div>
  );
}

function Section({ title, step, children }) {
  return (
    <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
      <div className="flex items-center gap-3 px-6 py-4 bg-slate-50 border-b border-slate-200">
        <div className="w-8 h-8 rounded-md bg-blue-600 text-white font-display font-bold text-sm flex items-center justify-center">
          {step}
        </div>
        <div className="text-sm font-semibold text-slate-900 font-display">
          {title}
        </div>
      </div>
      <div className="p-6">{children}</div>
    </div>
  );
}

function Field({ label, required, testId, children, className = "" }) {
  return (
    <div className={className} data-testid={testId}>
      <label
        className={`text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70 block mb-2 ${required ? "flex items-center gap-1" : ""}`}
      >
        {label}
        {required && <span className="text-red-500">*</span>}
      </label>
      {children}
    </div>
  );
}

function NumberedTextArea({
  name,
  value,
  onChange,
  placeholder,
  required,
  rows = 3,
  className = "",
  testId,
}) {
  const textareaRef = React.useRef(null);

  const onValChange = (e) => {
    let val = e.target.value;
    if (val.length === 1 && value === "") {
      val = "1. " + val;
    }
    onChange({ target: { name, value: val } });
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter") {
      e.preventDefault();
      const el = textareaRef.current;
      if (!el) return;
      const start = el.selectionStart;
      const end = el.selectionEnd;
      const val = el.value;

      const textBefore = val.substring(0, start);
      const textAfter = val.substring(end);

      const lines = textBefore.split("\n");
      const nextNum = lines.length + 1;
      const insertText = `\n${nextNum}. `;

      const newValue = textBefore + insertText + textAfter;

      onChange({ target: { name, value: newValue } });

      setTimeout(() => {
        if (el) {
          el.selectionStart = el.selectionEnd = start + insertText.length;
        }
      }, 0);
    }
  };

  return (
    <textarea
      ref={textareaRef}
      rows={rows}
      name={name}
      value={value}
      onChange={onValChange}
      onKeyDown={handleKeyDown}
      placeholder={placeholder}
      className={`flex min-h-[60px] w-full rounded-md border border-slate-200 bg-transparent px-3 py-2 text-sm shadow-sm focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-slate-950 disabled:cursor-not-allowed disabled:opacity-50 ${className}`}
      required={required}
      data-testid={testId}
    />
  );
}
